import React, { useMemo } from "react";
import {
  Box,
  MainButton,
  MainCard,
  MainText,
  MainTitle,
} from "app/components/common";
import { isWeb, isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import theme from "app/Theme/Theme";
import { LockedTimelineCard } from "app/components/common/Cards/TimelineActivation/LockedTimelineCard";
import moment from "moment";
import normalize from "app/utils/normalize";
import { useSelector } from "react-redux";
import { UnlockedTimelineCard } from "app/components/common/Cards/TimelineActivation/UnlockedTimelineCard";
import { IconBox } from "app/components/common/Box/IconBox/IconBox";
import CameraVideoIcon from "app/components/Icons/Video/CameraVideoIcon";
import FlashIcon from "app/components/Icons/FlashIcon";
import SurveyIcon from "app/components/Icons/SurveyIcon";
import {
  getCampaignActionGoal,
  getCampaignEndDate,
  getEventsWithActionGoals,
  getTotalDoneSteps,
} from "@microdoing/shared/services/userCampaign/selectors";
import { Image } from "react-native";
import Animated from "react-native-reanimated";

import i18n from "@microdoing/shared/i18n";
import {
  getLanguage,
  getProfileSettingsMbti,
} from "@microdoing/shared/services/profileSettings/selectors";
import { getMbtiOptions } from "app/scenes/UserProfile/MbtiSelectorScreen/MbtiLibrary";
import { getUserFirstName } from "@microdoing/shared/services/user/selectors";
import { useNavigation } from "@react-navigation/native";

export const TIME_LINE_CARD_HEIGHT = 70;

const certificateHat = require("../../../../../assets/images/certificate-hat.png");
const confettis = require("../../../../../assets/images/confettis.png");
const Jouvie3D = require("../../../../../assets/images/Jouvie3D.png");
const HappyJouvie = require("../../../../../assets/images/happyjouvie.png");

const ReanimatedBox = isWeb ? Box : Animated.createAnimatedComponent(Box);

interface IProsCertificate {
  endDate: Date;
  totalDoneSteps: number;
  campaignActionGoal: number;
  userMbti: string;
}

const CertificateBox = ({
  endDate,
  totalDoneSteps,
  campaignActionGoal,
  userMbti,
}: IProsCertificate) => {
  const language = useSelector(getLanguage);
  const userMbtiData = getMbtiOptions(language).find(
    item => item.key === userMbti,
  );
  const userFirstname = useSelector(getUserFirstName);

  const navigation = useNavigation();

  const navigateToCertificate = () => {
    navigation.navigate("CertificateScreen");
  };

  if (
    moment(endDate).isBefore(moment(), "day") ||
    totalDoneSteps >= campaignActionGoal
  ) {
    return (
      <Box>
        <Image
          style={{
            left: normalize(-16),
            top: normalize(-24),
            position: "absolute",
          }}
          source={confettis}
        />
        <MainCard
          alignItems="center"
          padding="xm"
          paddingTop="sm"
          backgroundColor="neutral.white"
          borderRadius={normalize(8)}
          shadowColor="brand.500"
          shadowRadius={normalize(29)}
          shadowOpacity={0.15}
          shadowOffset={{
            width: 0,
            height: 4,
          }}
          elevation={2}
        >
          <Image
            resizeMode="contain"
            source={HappyJouvie}
            style={{
              height: normalize(100),
              width: normalize(100),
              right: normalize(-30),
              top: normalize(-50),
              position: "absolute",
            }}
          />
          <Image
            source={certificateHat}
            style={{
              width: normalize(52),
              height: normalize(41),
              marginBottom: theme.spacing[2],
            }}
          />
          <MainTitle variant="l4-bold" marginBottom="xs" color="brand.500">
            {i18n.t("certificate-unlocked-title", { userName: userFirstname })}
          </MainTitle>
          <MainText
            variant="l3-medium"
            marginBottom="sm"
            textAlign="center"
            color="neutral.800"
          >
            {i18n.t("certificate-unlocked-description")}
          </MainText>
          <MainButton
            margin="null"
            onPress={navigateToCertificate}
            text={i18n.t("get-your-certification")}
          />
        </MainCard>
      </Box>
    );
  }

  return (
    <MainCard
      padding="xm"
      paddingTop="sm"
      borderWidth={2}
      borderColor="neutral.100"
      borderRadius={normalize(8)}
    >
      <Box alignItems="center">
        <Image
          source={certificateHat}
          style={{
            width: normalize(52),
            height: normalize(41),
            marginBottom: theme.spacing[2],
          }}
        />
        <MainTitle variant="l4-bold" marginBottom="xs" color="brand.500">
          {i18n.t("certificate-lock-title")}
        </MainTitle>
        <MainText variant="l3-medium" textAlign="center" color="neutral.800">
          {i18n.t("certificate-lock-description")}
        </MainText>
      </Box>
    </MainCard>
  );
};

interface IProps {
  reanimatedStyle: any;
}

const TimelineCards = ({ reanimatedStyle }: IProps) => {
  const events = useSelector(getEventsWithActionGoals);
  const endDate = useSelector(getCampaignEndDate);
  const totalDoneSteps = useSelector(getTotalDoneSteps);
  const campaignActionGoal = useSelector(getCampaignActionGoal);
  const userMbti = useSelector(getProfileSettingsMbti);
  const language = useSelector(getLanguage);

  const TimeLine = useMemo(() => {
    return events?.map((event, index) => {
      const eventDate = moment(event.date);
      const todayDate = moment();
      const eventTime = moment(event.date).format("HH:mm");
      const isTodayEvent = eventDate.isSame(todayDate, "day");

      const icon = (
        <IconBox
          backgroundColor={isTodayEvent ? "brand.500" : "brand.50"}
          isActive={isTodayEvent}
          icon={
            event?.type === "visio" ? (
              <CameraVideoIcon
                height={normalize(20)}
                width={normalize(20)}
                color={isTodayEvent ? "white" : theme.colors["brand.500"]}
              />
            ) : event?.type === "survey" ? (
              <SurveyIcon
                height={normalize(20)}
                width={normalize(20)}
                color={isTodayEvent ? "white" : theme.colors["brand.500"]}
              />
            ) : (
              <FlashIcon
                height={normalize(20)}
                width={normalize(20)}
                color={isTodayEvent ? "white" : theme.colors["brand.500"]}
              />
            )
          }
        />
      );

      return (
        <ReanimatedBox key={index} width="100%" marginBottom="sm">
          {isTodayEvent ? (
            <UnlockedTimelineCard
              iconBox={icon}
              time={eventTime}
              title={event?.title}
              url={event.type === "visio" ? event?.data?.url : ""}
              surveyId={event?.data?.survey}
            />
          ) : (
            <LockedTimelineCard
              iconBox={icon}
              date={event?.date}
              title={event?.title}
            />
          )}
        </ReanimatedBox>
      );
    });
  }, [events]);

  const renderCertificate = useMemo(() => {
    return (
      <CertificateBox
        endDate={endDate}
        totalDoneSteps={totalDoneSteps}
        campaignActionGoal={campaignActionGoal}
        userMbti={userMbti}
      />
    );
  }, [endDate, totalDoneSteps, campaignActionGoal, userMbti, language]);

  if (isWebDesktop) {
    return (
      <Box width="100%" paddingHorizontal="l" marginTop="sm">
        {TimeLine}
        {renderCertificate}
      </Box>
    );
  }

  return (
    <ReanimatedBox
      style={[
        {
          width: "100%",
          zIndex: 10,
          paddingBottom: normalize(50),
        },
        reanimatedStyle,
      ]}
    >
      {TimeLine}
      {renderCertificate}
    </ReanimatedBox>
  );
};

export default TimelineCards;
